import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { centeredSwal, showError } from '@/lib/alert'
import { usePageTitle } from '@/hooks/usePageTitle'
import { LegalModals } from '@/features/account/LegalModals'
import { sendOtp } from '@/features/account/otp'

/**
 * Port of Views/Account/ForgotPassword.cshtml +
 * wwwroot/js/app/account/forgotPassword.js.
 *
 * The page only sends an OTP; the POST /Account/ForgotPassword action it
 * declared as its form target did nothing but
 * `return RedirectToAction("ConfirmEmail")` and was never reached, because the
 * script intercepts the click and calls SendOTP instead. Only the OTP path is
 * ported.
 *
 * The original then dug the hash out of `data.Data.hash ?? data.data.hash ??
 * data.hash` — three guesses at a shape it did not control. The BFF returns
 * `redirectUrl` outright, so there is nothing left to guess at.
 */
export function ForgotPassword(): JSX.Element {
  usePageTitle('Forgot Password', 'auth')
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [wasValidated, setWasValidated] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault()

    const form = formRef.current
    if (!form) return

    setWasValidated(true)
    if (!form.checkValidity()) return

    setIsSubmitting(true)
    try {
      const data = await sendOtp(email, 'reset')

      if (!data.success) {
        await showError(data.message ?? 'Failed to send OTP. Please try again.')
        return
      }

      await centeredSwal({
        icon: 'success',
        title: 'OTP Sent!',
        text: 'Please check your email for the verification code.',
        timer: 2000,
        showConfirmButton: false,
        allowOutsideClick: false,
        timerProgressBar: true,
      })
      navigate(data.redirectUrl ?? '/Account/ConfirmEmail')
    } catch (error) {
      await showError(
        error instanceof Error ? error.message : 'Failed to send OTP. Please try again.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <div className="text-center">
        <img
          src="/lib/images/authentication/img-auth-fporgot-password.png"
          alt="Forgot Password"
          className="img-fluid mb-3"
        />
        <h4 className="f-w-500 mb-1">Forgot Password</h4>
        <p className="mb-3">
          Back to{' '}
          <Link to="/Account/Login" className="link-primary ms-1">
            Log in
          </Link>
        </p>
      </div>

      <form
        ref={formRef}
        id="forgotPasswordForm"
        onSubmit={(event) => void handleSubmit(event)}
        noValidate
        className={wasValidated ? 'was-validated' : undefined}
      >
        <div className="mb-3">
          <label className="form-label">Email Address</label>
          <input
            type="email"
            className="form-control"
            id="Email"
            name="Email"
            placeholder="Email Address"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="email"
          />
        </div>

        <div className="d-grid mt-3">
          <button
            type="submit"
            className="btn btn-primary"
            id="forgotPasswordBtn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send reset email'}
          </button>
        </div>

        <p className="text-muted text-center mt-3 mb-0" style={{ fontSize: 12 }}>
          By continuing, you agree to our{' '}
          <a href="#" className="link-primary" data-bs-toggle="modal" data-bs-target="#modalToS">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="#" className="link-primary" data-bs-toggle="modal" data-bs-target="#modalPrivacy">
            Privacy Policy
          </a>
        </p>
      </form>

      <LegalModals />
    </>
  )
}
